/* YoloLife — small shared UI controls. */
(function () {
  'use strict';

  const closeOpenDetails = (selector, except) => {
    document.querySelectorAll(selector).forEach((details) => {
      if (details !== except) details.open = false;
    });
  };

  const resetNavigatingProductCards = () => {
    document.querySelectorAll('.yl-species-card.is-navigating').forEach((card) => {
      card.classList.remove('is-navigating');
      card.setAttribute('aria-busy', 'false');
    });
  };

  const resetNavigatingPagination = () => {
    document.querySelectorAll('.yl-pagination.is-loading').forEach((pagination) => {
      pagination.classList.remove('is-loading');
      pagination.setAttribute('aria-busy', 'false');
    });
    document.querySelectorAll('.yl-collection__results.is-loading').forEach((results) => {
      results.classList.remove('is-loading');
      results.setAttribute('aria-busy', 'false');
    });
  };

  const getProductPlaceholderSrc = () => {
    const meta = document.querySelector('meta[name="yl-product-placeholder"]');
    return meta ? meta.getAttribute('content') : '';
  };

  const handleBrokenProductImage = (img) => {
    const placeholderSrc = getProductPlaceholderSrc();
    if (!img || !placeholderSrc || img.dataset.ylFallbackApplied === 'true') return;
    img.dataset.ylFallbackApplied = 'true';
    img.removeAttribute('srcset');
    img.removeAttribute('sizes');
    img.classList.add('yl-product-image--placeholder');
    img.src = placeholderSrc;
  };

  const getSubcategoryScrollStep = (grid) => {
    const item = grid.querySelector('.yl-subcat__item');
    if (!item) return Math.max(grid.clientWidth * 0.82, 180);

    const styles = window.getComputedStyle(grid);
    const gap = parseFloat(styles.columnGap || styles.gap || '0') || 0;
    return Math.max(item.getBoundingClientRect().width + gap, grid.clientWidth * 0.7);
  };

  const updateSubcategoryRail = (rail) => {
    const grid = rail.querySelector('[data-yl-subcat-grid]');
    const prev = rail.querySelector('[data-yl-subcat-nav="prev"]');
    const next = rail.querySelector('[data-yl-subcat-nav="next"]');
    if (!grid || !prev || !next) return;

    const maxScroll = Math.max(0, grid.scrollWidth - grid.clientWidth);
    const canScroll = maxScroll > 4;
    prev.hidden = !canScroll || grid.scrollLeft <= 4;
    next.hidden = !canScroll || grid.scrollLeft >= maxScroll - 4;
  };

  const scrollActiveSubcategoryIntoView = (rail, behavior = 'auto') => {
    const grid = rail.querySelector('[data-yl-subcat-grid]');
    if (!grid) return false;
    const active = grid.querySelector('.yl-subcat__item.is-active, .yl-subcat__card[aria-current="page"]')?.closest('.yl-subcat__item');
    if (!active) return false;

    const maxScroll = Math.max(0, grid.scrollWidth - grid.clientWidth);
    if (maxScroll <= 4) return false;

    updateSubcategoryRail(rail);
    const gridRect = grid.getBoundingClientRect();
    const activeRect = active.getBoundingClientRect();
    const prev = rail.querySelector('[data-yl-subcat-nav="prev"]');
    const next = rail.querySelector('[data-yl-subcat-nav="next"]');
    const startInset = prev && !prev.hidden ? prev.getBoundingClientRect().width : 0;
    const endInset = next && !next.hidden ? next.getBoundingClientRect().width : 0;
    const visibleStart = gridRect.left + startInset + 6;
    const visibleEnd = gridRect.right - endInset - 6;

    if (activeRect.left >= visibleStart && activeRect.right <= visibleEnd) return false;

    const target = active.offsetLeft + (active.offsetWidth / 2) - (grid.clientWidth / 2);
    grid.scrollTo({
      left: Math.max(0, Math.min(maxScroll, target)),
      behavior,
    });
    return true;
  };

  const queueSubcategoryRailUpdate = (rail) => {
    if (!rail || rail.dataset.ylSubcatUpdateQueued === 'true') return;
    rail.dataset.ylSubcatUpdateQueued = 'true';
    window.requestAnimationFrame(() => {
      rail.dataset.ylSubcatUpdateQueued = 'false';
      updateSubcategoryRail(rail);
    });
  };

  const bindSubcategoryRail = (rail) => {
    if (!rail || rail.dataset.ylSubcatBound === 'true') return;
    const grid = rail.querySelector('[data-yl-subcat-grid]');
    if (!grid) return;

    rail.dataset.ylSubcatBound = 'true';
    rail.querySelectorAll('[data-yl-subcat-nav]').forEach((button) => {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        const direction = button.dataset.ylSubcatNav === 'prev' ? -1 : 1;
        grid.scrollBy({
          left: getSubcategoryScrollStep(grid) * direction,
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
        });
        window.setTimeout(() => updateSubcategoryRail(rail), 260);
      });
    });

    grid.addEventListener('scroll', () => queueSubcategoryRailUpdate(rail), { passive: true });
    updateSubcategoryRail(rail);
    scrollActiveSubcategoryIntoView(rail);
    window.setTimeout(() => {
      scrollActiveSubcategoryIntoView(rail);
      updateSubcategoryRail(rail);
    }, 300);
  };

  const bindSubcategoryRails = () => {
    document.querySelectorAll('[data-yl-subcat-rail]').forEach(bindSubcategoryRail);
  };

  const alignSubcategoryRails = (behavior = 'auto') => {
    document.querySelectorAll('[data-yl-subcat-rail]').forEach((rail) => {
      bindSubcategoryRail(rail);
      scrollActiveSubcategoryIntoView(rail, behavior);
      updateSubcategoryRail(rail);
    });
  };

  const settleSubcategoryRails = () => {
    window.requestAnimationFrame(() => alignSubcategoryRails());
    [300, 900, 1800].forEach((delay) => {
      window.setTimeout(() => alignSubcategoryRails(), delay);
    });
  };

  const markNavigatingProductCard = (event) => {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    const link = event.target.closest('.yl-species-card a[href]');
    if (!link) return;
    if (link.target && link.target !== '_self') return;

    let url;
    try {
      url = new URL(link.href, window.location.href);
    } catch (_) {
      return;
    }
    if (url.origin !== window.location.origin) return;

    const card = link.closest('.yl-species-card');
    if (!card) return;
    card.classList.add('is-navigating');
    card.setAttribute('aria-busy', 'true');
  };

  const markNavigatingPagination = (event) => {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    const link = event.target.closest('[data-yl-pagination] a[href]');
    if (!link) return;
    if (link.target && link.target !== '_self') return;

    let url;
    try {
      url = new URL(link.href, window.location.href);
    } catch (_) {
      return;
    }
    if (url.origin !== window.location.origin) return;

    const pagination = link.closest('[data-yl-pagination]');
    const results = link.closest('[data-yl-collection-results]') || document.querySelector('[data-yl-collection-results]');
    if (pagination) {
      pagination.classList.add('is-loading');
      pagination.setAttribute('aria-busy', 'true');
    }
    if (results) {
      results.classList.add('is-loading');
      results.setAttribute('aria-busy', 'true');
    }
  };

  document.addEventListener('click', (event) => {
    markNavigatingPagination(event);
    markNavigatingProductCard(event);
    closeOpenDetails('details.yl-sort-menu__details[open], details.yl-choice-select[open]', event.target.closest('details'));
  });

  document.addEventListener('error', (event) => {
    const img = event.target;
    if (!img || !img.matches) return;
    if (!img.matches('.yl-species-card__img, .yl-product__hero, .yl-product__thumb img, .yl-psearch__thumb-img')) return;
    handleBrokenProductImage(img);
  }, true);

  window.addEventListener('pageshow', () => {
    resetNavigatingProductCards();
    resetNavigatingPagination();
    settleSubcategoryRails();
    document.querySelectorAll('.yl-species-card__img, .yl-product__hero, .yl-product__thumb img, .yl-psearch__thumb-img').forEach((img) => {
      if (img.complete && img.naturalWidth === 0) handleBrokenProductImage(img);
    });
  });

  window.addEventListener('load', settleSubcategoryRails, { once: true });

  window.addEventListener('resize', () => {
    document.querySelectorAll('[data-yl-subcat-rail]').forEach(queueSubcategoryRailUpdate);
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    const openDetails = document.querySelector('details.yl-sort-menu__details[open], details.yl-choice-select[open]');
    if (!openDetails) return;
    openDetails.open = false;
    const summary = openDetails.querySelector('summary');
    if (summary) summary.focus();
  });

  document.querySelectorAll('[data-yl-choice-select]').forEach((select) => {
    const field = select.parentElement ? select.parentElement.querySelector('input[type="hidden"]') : null;
    const current = select.querySelector('[data-yl-choice-current]');
    if (!field || !current) return;
    const decodeValue = (value) => {
      const parser = document.createElement('textarea');
      parser.innerHTML = value;
      return parser.value;
    };

    select.addEventListener('click', (event) => {
      const option = event.target.closest('[data-yl-choice-option]');
      if (!option) return;
      const value = decodeValue(option.getAttribute('data-value') || option.textContent.trim());
      field.value = value;
      current.textContent = value;
      select.querySelectorAll('[data-yl-choice-option]').forEach((candidate) => {
        const selected = candidate === option;
        candidate.classList.toggle('is-selected', selected);
        candidate.setAttribute('aria-selected', selected ? 'true' : 'false');
      });
      select.open = false;
    });
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', settleSubcategoryRails, { once: true });
  } else {
    settleSubcategoryRails();
  }
})();
